import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell, Pill, Activity, CheckCheck, X, Clock, Settings } from 'lucide-react';
import { doc, getDoc, setDoc, getDocs, deleteDoc, collection, query, orderBy, where, onSnapshot } from 'firebase/firestore';
import { cn } from '../lib/utils';
import { useLanguage } from '../functions/useLanguage';
import { useAuth } from '../context/AuthContext';
import { usePet } from '../context/PetContext';
import { useData } from '../context/DataContext';
import { db } from '../lib/firebase';
import {
  AppNotification,
  NotifKind,
  loadNotifications,
  saveNotifications,
  filterVisibleNotifications,
  clearAllNotifications,
} from '../lib/notificationService';
import { runReminderCheckNow, startReminderLoop, stopReminderLoop, updateReminderState } from '../lib/reminderEngine';
import { useSettings } from '../functions/useSettings';

interface NotificationCenterProps {
  embedded?: boolean;
  headless?: boolean;
  initService?: boolean;
  title?: string;
  onSettings?: () => void;
  panelClassName?: string;
  unreadDotClassName?: string;
}

// ── Icon per kind ────────────────────────────────────────────────────────────

function KindIcon({ kind }: { kind: NotifKind }) {
  const base = 'flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0';
  if (kind === 'med') return (
    <span className={base}>
      <Pill size={18} className="text-primary-brown" />
    </span>
  );
  if (kind === 'reading') return (
    <span className={base}>
      <Activity size={18} className="text-blue-500" />
    </span>
  );
  return (
    <span className={base}>
      <Clock size={18} className="text-amber-500" />
    </span>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export function NotificationCenter({
  embedded = false,
  headless = false,
  initService = true,
  title,
  onSettings,
  panelClassName,
  unreadDotClassName = 'bg-blue-500',
}: NotificationCenterProps) {
  const { t } = useLanguage();
  const { use24h } = useSettings();
  const { user } = useAuth();
  const { activePet, activePetId, activePetOwnerUid, isActivePetSubordinate } = usePet();
  const { medications } = useData();
  const activePetRef = useRef(activePet);
  const activePetIdRef = useRef(activePetId);
  useEffect(() => { activePetRef.current = activePet; activePetIdRef.current = activePetId; }, [activePet, activePetId]);
  const navigate = useNavigate();
  const [open, setOpen] = useState(embedded);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [acknowledgedIds, setAcknowledgedIds] = useState<Set<string>>(new Set());
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const notificationsRef = useRef<AppNotification[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [panelPos, setPanelPos] = useState<{ top: number; right: number } | null>(null);
  const mirroredNotifIdsRef = useRef<Set<string>>(new Set());
  // Keep a ref to the latest t() so the notification interval always uses current language
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; }, [t]);
  useEffect(() => { notificationsRef.current = notifications; }, [notifications]);

  useEffect(() => {
    if (!initService || !user?.id) {
      stopReminderLoop();
      return;
    }

    startReminderLoop({
      medications,
      petId: activePetId ?? null,
      petName: activePet?.name ?? null,
    });

    return () => {
      stopReminderLoop();
    };
  }, [initService, user?.id]);

  useEffect(() => {
    updateReminderState({
      medications,
      petId: activePetId ?? null,
      petName: activePet?.name ?? null,
    });
  }, [medications, activePetId, activePet?.name]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'CHECK_REMINDERS') {
        runReminderCheckNow();
      }
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    if (!('periodicSync' in window.ServiceWorkerRegistration.prototype)) return;

    void navigator.serviceWorker.ready.then(async registration => {
      const periodicSync = (registration as ServiceWorkerRegistration & {
        periodicSync?: {
          register: (tag: string, options: { minInterval: number }) => Promise<void>;
          getTags?: () => Promise<string[]>;
        };
      }).periodicSync;

      if (!periodicSync?.register) return;

      try {
        const tags = periodicSync.getTags ? await periodicSync.getTags() : [];
        if (!tags.includes('koda-reminder-check')) {
          await periodicSync.register('koda-reminder-check', { minInterval: 15 * 60 * 1000 });
        }
      } catch {
        // optional enhancement only
      }
    });
  }, []);

  useEffect(() => {
    if (embedded) setOpen(true);
  }, [embedded]);

  // Hydrate local reminder settings from cloud so reminder behaviour is
  // consistent across devices for the same account.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const syncReminderSettingsFromCloud = async () => {
      try {
        const prefSnap = await getDoc(doc(db, 'users', user.id, 'notification_preferences', 'default'));
        if (!prefSnap.exists() || cancelled) return;

        const data = prefSnap.data() as {
          notification_setting?: 'all' | 'logs' | 'meds' | 'none';
          log_reminder_am?: string;
          log_reminder_pm?: string;
        };

        const updates: Record<string, unknown> = {};

        if (data.notification_setting) updates.notificationSetting = data.notification_setting;
        if (data.log_reminder_am) updates.logReminderAM = data.log_reminder_am;
        if (data.log_reminder_pm) updates.logReminderPM = data.log_reminder_pm;

        if (Object.keys(updates).length > 0) {
          const current = JSON.parse(localStorage.getItem('koda_settings') || '{}');
          localStorage.setItem('koda_settings', JSON.stringify({ ...current, ...updates }));
          window.dispatchEvent(new Event('storage'));
        }
      } catch {
        // non-fatal, keep local behaviour
      }
    };

    void syncReminderSettingsFromCloud();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Mirror local notifications to Firestore so co-carers and owners stay in sync.
  const mirrorNotificationToCloud = useCallback((notif: AppNotification) => {
    const petId = notif.petId ?? activePetIdRef.current ?? null;
    const ownerUid = activePetOwnerUid ?? user?.id ?? null;
    const mirrorKey = `${notif.id}:${petId ?? 'none'}:${ownerUid ?? 'none'}`;
    if (mirroredNotifIdsRef.current.has(mirrorKey)) return;

    if (user?.id) {
      const { activePetId: _pid, ...rest } = notif as AppNotification & { activePetId?: string };
      const notifData = {
        ...rest,
        petId,
        petName: notif.petName ?? null,
        title: notif.title,
        body: notif.body,
        rawBody: notif.rawBody ?? null,
        timestamp: notif.timestamp,
      };

      mirroredNotifIdsRef.current.add(mirrorKey);

      // Write to user-level collection (owner's own view)
      void setDoc(doc(db, 'users', user.id, 'notifications', notif.id), notifData)
        .catch(() => { /* non-fatal */ });

      // ALSO write to owner pet-level collection so all co-carers share one stream.
      if (ownerUid && petId) {
        void setDoc(doc(db, 'users', ownerUid, 'pets', petId, 'notifications', notif.id), notifData)
          .catch(() => { /* non-fatal */ });
      }
    }
  }, [activePetOwnerUid, user?.id]);

  // React to local notification writes (from reminder engine) and mirror them
  // to Firestore for cross-account visibility.
  useEffect(() => {
    const onNotificationsUpdated = () => {
      const latest = loadNotifications();
      setNotifications(prev => {
        const latestIds = new Set(latest.map(n => n.id));
        const firestoreOnly = prev.filter(n => !latestIds.has(n.id));
        return filterVisibleNotifications([...latest, ...firestoreOnly])
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, 50);
      });
      latest.forEach(n => mirrorNotificationToCloud(n));
    };

    window.addEventListener('koda-notifications-updated', onNotificationsUpdated);
    return () => window.removeEventListener('koda-notifications-updated', onNotificationsUpdated);
  }, [mirrorNotificationToCloud]);

  // Load notifications on mount — seed from localStorage, then hydrate from
  // Firestore if localStorage is empty (e.g. after a cache/PWA clear)
  useEffect(() => {
    const local = loadNotifications();
    if (local.length > 0) {
      setNotifications(local);
      return;
    }
    if (!user?.id) return;
    const cutoff = Date.now() - 5 * 24 * 60 * 60 * 1000;
    void (async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, 'users', user.id, 'notifications'),
            where('timestamp', '>=', cutoff),
            orderBy('timestamp', 'desc'),
          ),
        );
        const fetched: AppNotification[] = snap.docs.map(d => d.data() as AppNotification);
        if (fetched.length > 0) {
          saveNotifications(fetched);
          setNotifications(fetched);
        }
      } catch {
        // non-fatal — keep empty list
      }
    })();
  }, [user?.id]);

  // Owner side: backfill top-level notifications → pet-level subcollection once,
  // then keep a live onSnapshot to pick up ack changes written by co-carers.
  useEffect(() => {
    if (isActivePetSubordinate || !user?.id || !activePetId) return;

    // One-time backfill
    void getDocs(collection(db, 'users', user.id, 'notifications')).then(snap => {
      snap.docs.forEach(d => {
        const notif = d.data() as AppNotification;
        if (notif.petId === activePetId) {
          void setDoc(doc(db, 'users', user.id, 'pets', activePetId, 'notifications', d.id), notif).catch(() => {});
        }
      });
    }).catch(() => {});

    // Real-time listener: catch ack patches written by subordinates
    const unsub = onSnapshot(
      collection(db, 'users', user.id, 'pets', activePetId, 'notifications'),
      snap => {
        snap.docChanges().forEach(change => {
          if (change.type === 'modified') {
            const data = { ...(change.doc.data() as AppNotification), id: change.doc.id };
            if (data.acknowledgedAt) {
              const next = notificationsRef.current.map(n =>
                n.id === data.id
                  ? { ...n, acknowledgedAt: data.acknowledgedAt, acknowledgedBy: data.acknowledgedBy, read: true }
                  : n
              );
              setNotifications(next);
              setAcknowledgedIds(prev => new Set([...prev, data.id]));
              saveNotifications(next);
            }
          }
        });
      },
      () => {} // non-fatal
    );
    return unsub;
  }, [user?.id, activePetId, isActivePetSubordinate]);

  // Subordinate side: live onSnapshot on owner's pet-level notifications.
  // Gets initial data AND real-time ack updates from the owner or other co-carers.
  useEffect(() => {
    if (!isActivePetSubordinate || !activePetOwnerUid || !activePetId || !user?.id) return;
    const cutoff = Date.now() - 5 * 24 * 60 * 60 * 1000;

    const unsub = onSnapshot(
      collection(db, 'users', activePetOwnerUid, 'pets', activePetId, 'notifications'),
      snap => {
        const existing = loadNotifications();
        // Build a map of locally-known acks so we never lose an ack that's
        // still in-flight to Firestore or was written by this device.
        const localAckMap = new Map(
          existing.filter(n => n.acknowledgedAt).map(n => [n.id, { acknowledgedAt: n.acknowledgedAt, acknowledgedBy: n.acknowledgedBy }])
        );
        const localReadIds = new Set(existing.filter(n => n.read).map(n => n.id));

        const ownerNotifs = snap.docs
          .map(d => {
            const data = d.data() as AppNotification;
            const localAck = localAckMap.get(d.id);
            // Prefer local ack — it might be fresher than Firestore (still in-flight)
            const acknowledgedAt = localAck?.acknowledgedAt ?? data.acknowledgedAt;
            const acknowledgedBy = localAck?.acknowledgedBy ?? data.acknowledgedBy;
            return {
              ...data,
              id: d.id,
              acknowledgedAt,
              acknowledgedBy,
              // Per-user read state: acknowledged = read; otherwise use local record
              read: acknowledgedAt ? true : localReadIds.has(d.id),
            };
          })
          .filter(n => n.timestamp >= cutoff)
          .sort((a, b) => b.timestamp - a.timestamp);

        // Merge with local-only entries (e.g. daily log reminders on this device)
        const ownerIds = new Set(ownerNotifs.map(n => n.id));
        const localOnly = existing.filter(n => !ownerIds.has(n.id));
        const merged = filterVisibleNotifications([...ownerNotifs, ...localOnly])
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, 50);

        saveNotifications(merged);
        setNotifications(merged);
        // Accumulate — never remove an ack that was set locally
        setAcknowledgedIds(prev => new Set([...prev, ...merged.filter(n => !!n.acknowledgedAt).map(n => n.id)]));
      },
      err => console.warn('[NotificationCenter] subordinate sync failed:', err)
    );
    return unsub;
  }, [isActivePetSubordinate, activePetOwnerUid, activePetId, user?.id]);



  // Sync with localStorage periodically (in case of changes from other tabs).
  // Merge rather than replace so Firestore-loaded entries (e.g. from the owner's
  // pet-level collection) are not wiped when localStorage is empty.
  useEffect(() => {
    const syncInterval = setInterval(() => {
      const stored = loadNotifications();
      if (stored.length === 0) return; // nothing in storage — keep current state
      setNotifications(prev => {
        const storedIds = new Set(stored.map(n => n.id));
        const firestoreOnly = prev.filter(n => !storedIds.has(n.id));
        if (firestoreOnly.length === 0) return stored; // fast path: no Firestore-only entries
        return filterVisibleNotifications([...stored, ...firestoreOnly]).sort((a, b) => b.timestamp - a.timestamp).slice(0, 50);
      });
    }, 5000);

    return () => clearInterval(syncInterval);
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  /** Re-derive title/body from kind so language changes are reflected immediately,
   *  even for notifications already persisted in localStorage. */
  const getDisplay = (n: AppNotification): { title: string; body: string } => {
    if (n.kind === 'med') {
      const body = n.rawBody
        ? t('notifications.medReminderBody').replace('{meds}', n.rawBody)
        : n.body;
      return { title: t('notifications.medReminder'), body };
    }
    if (n.kind === 'reminder') {
      return {
        title: t('notifications.dailyReminder'),
        body: t('notifications.dailyReminderBody'),
      };
    }
    return { title: n.title, body: n.body };
  };

  const parseAndFormatTime = (value: string): string => {
    const raw = (value ?? '').trim();
    if (!raw) return '';

    // 24h input e.g. 23:00
    const m24 = raw.match(/^(\d{1,2}):(\d{2})$/);
    if (m24) {
      const hour = Math.min(23, Math.max(0, Number(m24[1])));
      const minute = Math.min(59, Math.max(0, Number(m24[2])));
      if (use24h) return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const h12 = hour % 12 || 12;
      return `${h12}:${String(minute).padStart(2, '0')} ${ampm}`;
    }

    // 12h input e.g. 11:00 PM
    const m12 = raw.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
    if (m12) {
      const hour12 = Math.min(12, Math.max(1, Number(m12[1])));
      const minute = Math.min(59, Math.max(0, Number(m12[2])));
      const ampm = m12[3].toUpperCase();
      const hour24 = ampm === 'PM' ? (hour12 % 12) + 12 : (hour12 % 12);
      if (use24h) return `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      return `${hour12}:${String(minute).padStart(2, '0')} ${ampm}`;
    }

    return raw;
  };

  const formatNotificationDateTime = (timestamp: number, fallbackTime: string) => {
    const d = new Date(timestamp);
    if (Number.isNaN(d.getTime())) {
      return { date: '', time: parseAndFormatTime(fallbackTime) };
    }
    return {
      date: d.toLocaleDateString([], { month: 'short', day: 'numeric' }),
      // Prefer scheduled time stored on the notification (e.g. meds at 23:00)
      // and only fall back to the creation timestamp if unavailable.
      time: parseAndFormatTime(fallbackTime) || d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: !use24h }),
    };
  };

  // Compute portal position from button's viewport rect
  useEffect(() => {
    if (embedded || headless) return;
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPanelPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    }
  }, [open, embedded, headless]);

  // Close on outside click
  useEffect(() => {
    if (embedded || headless) return;
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, embedded, headless]);

  const handleMarkAllRead = () => {
    const next = notificationsRef.current.map(n => ({ ...n, read: true }));
    saveNotifications(next);
    setNotifications(next);
  };

  const handleDismiss = (id: string) => {
    const next = notificationsRef.current.filter(n => n.id !== id);
    saveNotifications(next);
    setNotifications(next);
  };

  const handleMarkRead = (id: string) => {
    const target = notificationsRef.current.find(n => n.id === id);
    if (!target || target.read) return;
    const next = notificationsRef.current.map(n => n.id === id ? { ...n, read: true } : n);
    saveNotifications(next);
    setNotifications(next);
  };

  const handleClearAll = () => {
    if (!confirmClearAll) {
      setConfirmClearAll(true);
      setTimeout(() => setConfirmClearAll(false), 3000);
      return;
    }
    setConfirmClearAll(false);
    clearAllNotifications(activePetId ?? undefined);
    setNotifications([]);

    // Also delete from Firestore to prevent repopulation
    if (user?.id && activePetId) {
      // Delete from owner's pet-level collection
      if (activePetOwnerUid) {
        void getDocs(collection(db, 'users', activePetOwnerUid, 'pets', activePetId, 'notifications')).then(snap => {
          snap.docs.forEach(d => {
            void deleteDoc(d.ref).catch(() => {});
          });
        }).catch(() => {});
      }
    }
  };

  const handleAcknowledge = async (n: AppNotification) => {
    const now = Date.now();
    const ackedBy = user?.full_name || user?.email || 'unknown';
    const patch = { acknowledgedAt: now, acknowledgedBy: ackedBy, read: true as const };
    const next = notificationsRef.current.map(x => x.id === n.id ? { ...x, ...patch } : x);

    // 1. Update local state immediately
    setAcknowledgedIds(prev => new Set([...prev, n.id]));
    setNotifications(next);

    // 2. Persist to localStorage BEFORE Firestore writes so any onSnapshot
    //    that fires during the write finds the ack already in the localAckMap.
    saveNotifications(next);

    if (user?.id) {
      // Personal ack record (own device restore)
      void setDoc(doc(db, 'users', user.id, 'reminder_acks', n.id), {
        acknowledgedAt: now,
        acknowledgedBy: ackedBy,
        petId: n.petId ?? null,
        petName: n.petName ?? null,
        title: n.title,
        body: n.body,
        rawBody: n.rawBody ?? null,
        timestamp: n.timestamp,
      }).catch(() => {});
      // Patch the SHARED pet-level notification so both owner and all co-carers
      // see "Given by [name]" in real-time via their onSnapshot listener.
      if (activePetOwnerUid && activePetId) {
        void setDoc(
          doc(db, 'users', activePetOwnerUid, 'pets', activePetId, 'notifications', n.id),
          { acknowledgedAt: now, acknowledgedBy: ackedBy },
          { merge: true },
        ).catch(() => {});
      }
    }
  };

  const panel = (
    <div
      ref={panelRef}
      className={cn(
        'rounded-2xl flex flex-col overflow-hidden',
        embedded ? 'w-full max-h-[70vh] bg-white' : 'fixed w-[340px] max-h-[80vh] z-[9999] animate-in fade-in zoom-in-95 duration-150 origin-top-right border border-gray-200 bg-white',
        panelClassName,
      )}
      style={embedded ? undefined : { top: panelPos?.top, right: panelPos?.right }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-base text-text-primary">
            {title ?? t('notifications.title')}
          </h3>
          {unreadCount > 0 && (
            <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-text-secondary">
              {unreadCount} {t('notifications.new')}
            </span>
          )}
        </div>
        {(unreadCount > 0 || onSettings) && (
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="flex items-center gap-1 text-xs font-medium transition-colors text-primary-brown hover:text-blue-700"
              >
                <CheckCheck size={14} />
                {t('notifications.markAllRead')}
              </button>
            )}
            {onSettings && (
              <button
                onClick={onSettings}
                className="p-1.5 rounded-full transition-colors hover:bg-gray-100 text-gray-400 hover:text-text-secondary"
                aria-label={t('notifications.settingsLabel')}
              >
                <Settings size={16} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* List */}
      <div className="overflow-y-auto flex-1">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 gap-3 text-gray-400">
            <Bell size={36} className="opacity-30" />
            <p className="text-sm font-medium">{t('notifications.allCaughtUp')}</p>
          </div>
        ) : (() => {
          const cutoff5d = Date.now() - 5 * 24 * 60 * 60 * 1000;
          const visible = notifications.filter(n =>
            n.timestamp >= cutoff5d &&
            (!activePetId || !n.petId || n.petId === activePetId)
          );
          // Group by calendar date
          const groups: { label: string; items: AppNotification[] }[] = [];
          for (const n of visible) {
            const dayLabel = new Date(n.timestamp).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
            const last = groups[groups.length - 1];
            if (last && last.label === dayLabel) { last.items.push(n); }
            else { groups.push({ label: dayLabel, items: [n] }); }
          }
          return groups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 gap-3 text-gray-400">
              <Bell size={36} className="opacity-30" />
              <p className="text-sm font-medium">{t('notifications.allCaughtUp')}</p>
            </div>
          ) : (
            <>
              {groups.map(group => (
                <div key={group.label}>
                  <div className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-secondary bg-gray-50 border-b border-gray-100">
                    {group.label}
                  </div>
                  {group.items.map((n, i) => {
            const isAcked = !!n.acknowledgedAt || acknowledgedIds.has(n.id);
            return (
            <div
              key={n.id}
              onClick={() => {
                handleMarkRead(n.id);
                if (!embedded) {
                  setOpen(false);
                  navigate('/library/reminders');
                }
              }}
              className={cn(
                'flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors group hover:bg-primary-brown/5',
                i < group.items.length - 1 && 'border-b border-gray-50',
                !n.read && 'bg-primary-brown/10',
              )}
            >
              <KindIcon kind={n.kind} />

              {(() => {
                const { title, body } = getDisplay(n);
                const { date, time } = formatNotificationDateTime(n.timestamp, n.time);
                return (
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    <p className={cn(
                      'text-sm font-semibold leading-snug truncate',
                      n.read ? 'text-text-secondary' : 'text-text-primary',
                    )}>
                      {title}
                    </p>
                    {n.kind === 'med' && n.petName && (
                      <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary-brown/10 text-primary-brown leading-tight">
                        {n.petName}
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] flex-shrink-0 text-gray-400 text-right leading-tight">
                    {date ? <>{date}<br />{time}</> : time}
                  </span>
                </div>
                <p className="text-xs mt-0.5 leading-snug text-text-secondary">
                  {body}
                </p>
                {n.kind === 'med' && (
                  <button
                    onClick={e => { e.stopPropagation(); void handleAcknowledge(n); }}
                    disabled={isAcked}
                    className={cn(
                      'mt-1.5 inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full transition-all duration-300',
                      isAcked
                        ? 'bg-green-100 text-green-700 cursor-default'
                        : 'bg-primary-brown/10 text-primary-brown hover:bg-primary-brown/20',
                    )}
                  >
                    {isAcked ? (() => {
                      const at = n.acknowledgedAt
                        ? new Date(n.acknowledgedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: !use24h })
                        : '';
                      const by = n.acknowledgedBy || t('doses.givenBy');
                      return `✓ ${t('doses.givenBy')} ${by}${at ? ` · ${at}` : ''}`;
                    })() : t('doses.markAsGiven')}
                  </button>
                )}
              </div>
                );
              })()}

              {/* Dismiss */}
              <button
                onClick={e => { e.stopPropagation(); handleDismiss(n.id); }}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full flex-shrink-0 mt-0.5 hover:bg-gray-200 text-gray-400"
                aria-label={t('notifications.dismiss')}
              >
                <X size={13} />
              </button>

              {/* Unread dot */}
              {!n.read && (
                <span className={cn('w-2 h-2 rounded-full flex-shrink-0 mt-1.5', unreadDotClassName)} />
              )}
            </div>
            );
          })}
                </div>
              ))}
            </>
          );
        })()}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="px-4 py-2.5 border-t text-center border-gray-100">
          <button
            onClick={handleClearAll}
            className={cn(
              'text-xs font-medium transition-all duration-200',
              confirmClearAll
                ? 'text-error-red font-semibold'
                : 'text-gray-400 hover:text-text-secondary',
            )}
          >
            {confirmClearAll ? t('notifications.clearAllConfirm') : t('notifications.clearAll')}
          </button>
        </div>
      )}
    </div>
  );

  if (headless) return null;

  return (
    <div className={embedded ? 'w-full' : 'relative'}>
      {/* Bell button */}
      {!embedded && (
        <button
          ref={buttonRef}
          onClick={() => setOpen(o => !o)}
          className={cn(
            'relative p-2 rounded-full transition-colors hover:bg-gray-100 text-text-secondary',
            open && 'bg-gray-100',
          )}
          aria-label={t('notifications.bellLabel')}
        >
          <Bell size={22} />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-error-red text-white text-[10px] font-bold flex items-center justify-center leading-none">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {embedded && panel}
      {!embedded && open && panelPos && ReactDOM.createPortal(panel, document.body)}
    </div>
  );
}
